import { MessageOutlined } from '@ant-design/icons';
import { Button, Tooltip, Badge } from 'antd';
import { createStyles } from 'antd-style';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getChatRoomList, getChatRoomSimpleList, getCustomerCount, CustomerCountResponseDto } from '@/services/ant-design-pro/chat-room';
import { notifyNewChatRoom, sendNotification } from '@/utils/notificationUtils';

const useStyles = createStyles(({ token }) => {
    return {
        chatButton: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            // color: '#fff',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                transform: 'scale(1.05)',
            },
        },
    };
});

interface ChatButtonProps {
    onClick: () => void;
    onClearNotification?: () => void;
}

export const ChatButton: React.FC<ChatButtonProps> = ({ onClick, onClearNotification }) => {
    const { styles } = useStyles();

    const [hasNewChatRooms, setHasNewChatRooms] = useState<boolean>(false);
    const [previousRoomCount, setPreviousRoomCount] = useState<number | null>(0); // null表示未初始化
    const [previousCustomerCounts, setPreviousCustomerCounts] = useState<Map<string, number>>(new Map());
    const [isFirstCustomerCountCheck, setIsFirstCustomerCountCheck] = useState<boolean>(true);
    const [currentChatRooms, setCurrentChatRooms] = useState<API.ChatRoomListItem[]>([]);

    // 检查未读消息和新增聊天室
    const checkUnreadMessages = useCallback(async () => {
        try {
            const response = await getChatRoomSimpleList({
                page: 1,
                limit: 100, // 增加限制以获取更多数据
            });

            if (response.code === 0 && response.data) {
                const currentRooms = response.data.data;
                const currentRoomCount = currentRooms.length;

                // 存储聊天室列表数据供其他函数使用
                setCurrentChatRooms(currentRooms);

                // 检查是否有新增的聊天室（通过数量对比）
                let hasNewRooms = false;

                // 只有在已经初始化（不是第一次加载）且当前数量大于之前数量时，才判定为新增
                if (previousRoomCount !== null && currentRoomCount > previousRoomCount) {
                    hasNewRooms = true;
                }

                // 更新之前的聊天室数量
                setPreviousRoomCount(currentRoomCount);

                // 只有在检测到新增聊天室时才设置为true，不会重置为false
                if (hasNewRooms) {
                    // console.log('检测到新增聊天室');
                    setHasNewChatRooms(true);
                    notifyNewChatRoom(currentRoomCount);
                }
            }
        } catch (error) {
            console.error('检查未读消息失败:', error);
            // 发生错误时清空聊天室列表
            setCurrentChatRooms([]);
        }
    }, [previousRoomCount]);

    // 检查客户消息数量（使用已获取的聊天室列表）
    const checkCustomerMessageCounts = useCallback(async () => {
        try {
            // 使用已存储的聊天室列表，无需重复API调用
            if (currentChatRooms.length === 0) {
                return; // 如果没有聊天室数据，直接返回
            }

            // 提取进行中的订单号（假设状态为 'ongoing' 或 'in_progress'，根据实际业务调整）
            const ongoingOrderNos = currentChatRooms
                .filter(room => room.status === 'pending')
                .map(room => room.order_no)
                .filter(orderNo => orderNo); // 过滤掉空值
            console.log('ongoingOrderNos', ongoingOrderNos);
            if (ongoingOrderNos.length === 0) {
                return;
            }

            // 查询客户消息数量
            const customerCountResponse = await getCustomerCount({
                order_nos: ongoingOrderNos
            });

            if (customerCountResponse.code === 0 && customerCountResponse.data) {
                const currentCounts = customerCountResponse.data;
                const newCustomerCounts = new Map<string, number>();

                // 检查是否有新增的客户消息
                let hasNewCustomerMessages = false;
                const newMessageOrders: string[] = [];

                currentCounts.forEach(countData => {
                    const { order_no, customer_count } = countData;
                    newCustomerCounts.set(order_no, customer_count);

                    // 如果不是第一次检查，比较消息数量
                    if (!isFirstCustomerCountCheck) {
                        const previousCount = previousCustomerCounts.get(order_no) || 0;
                        if (customer_count > previousCount) {
                            hasNewCustomerMessages = true;
                            newMessageOrders.push(order_no);
                        }
                    }
                });

                // 更新状态
                setPreviousCustomerCounts(newCustomerCounts);

                if (isFirstCustomerCountCheck) {
                    setIsFirstCustomerCountCheck(false);
                }

                // 如果有新的客户消息，发送通知
                if (hasNewCustomerMessages && newMessageOrders.length > 0) {
                    console.log('检测到新的客户消息，订单:', newMessageOrders);

                    // 发送通知
                    await sendNotification(
                        {
                            title: '新的客户消息',
                            body: `订单 ${newMessageOrders.join(', ')} 有新的客户消息`,
                            tag: 'customer-message-notification',
                            autoCloseDelay: 5000
                        },
                        {},
                        {
                            soundEnabled: true,
                            desktopNotificationEnabled: true
                        }
                    );
                }
            }
        } catch (error) {
            console.error('检查客户消息数量失败:', error);
        }
    }, [currentChatRooms, previousCustomerCounts, isFirstCustomerCountCheck]);

    // 处理点击事件
    const handleClick = () => {
        // 用户点击查看聊天后，清除新增聊天室提醒
        setHasNewChatRooms(false);

        // 调用父组件的onClick回调
        onClick();

        // 如果有清除通知的回调，也调用它
        onClearNotification?.();
    };

    // 使用 ref 来存储最新的函数引用，避免依赖问题
    const checkCustomerMessageCountsRef = useRef(checkCustomerMessageCounts);
    checkCustomerMessageCountsRef.current = checkCustomerMessageCounts;

    // 当聊天室列表更新后，检查客户消息数量
    useEffect(() => {
        if (currentChatRooms.length > 0) {
            // console.log('currentChatRooms', currentChatRooms.length);
            // 使用 ref 调用函数，避免无限循环
            checkCustomerMessageCountsRef.current();
        }
    }, [currentChatRooms]); // 只依赖 currentChatRooms

    // 设置轮询
    useEffect(() => {
        // 初始检查 - 延迟执行避免无限循环
        setTimeout(() => {
            checkUnreadMessages();
        }, 1000);

        // 设置轮询，每5秒检查一次聊天室列表
        const interval = setInterval(() => {
            checkUnreadMessages();
        }, 5000);

        // 清理定时器
        return () => {
            clearInterval(interval);
        };
    }, [checkUnreadMessages]); // 移除checkCustomerMessageCounts依赖

    return (
        <Tooltip title="打开聊天管理" placement="bottom">
            <Badge dot={hasNewChatRooms} offset={[-5, 5]}>
                <div className={styles.chatButton} onClick={handleClick}>
                    <MessageOutlined style={{ fontSize: '18px' }} />
                </div>
            </Badge>
        </Tooltip>
    );
}; 